import React, { useState } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation.ts';
import { ChevronDownIcon } from './Icons.tsx';

const faqData = [
  {
    question: "What kind of equipment can I manage with Shield?",
    answer: "You can manage virtually any physical asset. Our customers track everything from Personal Protective Equipment (PPE) and power tools to heavy machinery, vehicles, and IT hardware. The system is fully customizable to your asset types."
  },
  {
    question: "Is my equipment data secure?",
    answer: "Absolutely. We employ end-to-end encryption for all data in transit and at rest. Our infrastructure is SOC2 Type II compliant, ensuring your inventory and compliance data is protected to the highest standards."
  },
  {
    question: "Can I import my existing equipment list?",
    answer: "Yes. Getting started is easy. You can use our bulk import feature to upload your entire equipment registry from an Excel or CSV file in minutes. Our onboarding team is also here to help you get set up."
  },
  {
    question: "How does the QR code system work?",
    answer: "You can generate and print unique QR codes from within Shield to affix to your equipment. When a field technician scans the code with our mobile app, it instantly pulls up that item's complete profile, logbook, and inspection history."
  }
];

interface FaqItemProps {
  question: string;
  answer: string;
}

const FaqItem: React.FC<FaqItemProps> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-200 dark:border-gray-800 py-6">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center text-left"
      >
        <h4 className="text-lg font-medium text-gray-900 dark:text-white">{question}</h4>
        <ChevronDownIcon className={`h-6 w-6 text-gray-500 dark:text-gray-400 transition-transform duration-300 ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>
      <div
        className={`grid transition-all duration-500 ease-in-out ${
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
            <p className="text-gray-700 dark:text-gray-400 pt-4">{answer}</p>
        </div>
      </div>
    </div>
  );
};

const Faq: React.FC = () => {
    const { ref, isVisible } = useScrollAnimation<HTMLDivElement>();
  return (
    <section id="faq" ref={ref} className="py-24 bg-gray-100 dark:bg-black/20">
      <div className={`container mx-auto px-6 scroll-animation ${isVisible ? 'visible' : ''}`}>
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">Frequently Asked Questions</h2>
          <p className="text-gray-700 dark:text-gray-400">
            Have questions? We have answers. If you can't find what you're looking for, feel free to contact us.
          </p>
        </div>
        <div className="max-w-3xl mx-auto bg-white dark:bg-black/20 rounded-2xl p-4 md:p-8 shadow-2xl shadow-orange-600/20 dark:shadow-2xl dark:shadow-[#FF4D00]/20">
          {faqData.map((item, index) => (
            <FaqItem key={index} question={item.question} answer={item.answer} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Faq;